import { UserPermission } from '@contracts/users/UserPermission';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@src/common/components/button/Button';
import { PageLayout } from '@src/common/components/Page';
import { Stack } from '@src/common/components/Stack';
import { TextInput } from '@src/common/components/TextInput';
import { useToast } from '@src/common/hooks/useToast';
import { theme } from '@src/common/styles/Theme';
import type { User } from '@src/manageUsers/domain/User';
import { EditUserFormInputs } from '@src/manageUsers/EditUserFormInputs';
import { useUserUpdate } from '@src/manageUsers/hooks/useUserUpdate';
import { ControllerFormCheckbox } from '@src/orders/PermitFormSections/FormCheckbox';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface Props {
  user: User;
}

export const EditUser = ({ user }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showSuccessToast } = useToast();

  const { updateUser, isLoading } = useUserUpdate(user.id, {
    onSuccess: () => {
      navigate('/users');
      showSuccessToast(t('editUserSuccess'));
    },
  });

  const {
    register,
    control,
    formState: { errors },
    handleSubmit,
  } = useForm({
    resolver: yupResolver(EditUserFormInputs),
    defaultValues: {
      name: user.name,
      extension: user.extension,
      canProvideAiFeedback: user.hasPermission(
        UserPermission.PROVIDE_AI_FEEDBACK,
      ),
    },
  });

  const submitUser: SubmitHandler<EditUserFormInputs> = ({
    name,
    extension,
    canProvideAiFeedback,
  }) => {
    const permissions = new Set(user.permissions);
    if (canProvideAiFeedback) {
      permissions.add(UserPermission.PROVIDE_AI_FEEDBACK);
    } else {
      permissions.delete(UserPermission.PROVIDE_AI_FEEDBACK);
    }

    updateUser({ name, extension, permissions });
  };

  return (
    <PageLayout title={t('editUser')}>
      <form onSubmit={handleSubmit(submitUser)}>
        <Stack
          direction="column"
          alignItems="stretch"
          width="400px"
          gap={theme.spacing['3']}
        >
          <TextInput
            label={t('name')}
            error={errors.name && t(`formErrors.${errors.name.message}`)}
            {...register('name')}
          />
          <TextInput
            label={t('extension')}
            error={
              errors.extension && t(`formErrors.${errors.extension.message}`)
            }
            {...register('extension')}
          />
          <ControllerFormCheckbox
            control={control}
            fieldName="canProvideAiFeedback"
            label={t('allowToProvideAiFeedback')}
          />

          <Stack direction="row" justifyContent="center">
            <Button disabled={isLoading} type="submit">
              {t('update')}
            </Button>
          </Stack>
        </Stack>
      </form>
    </PageLayout>
  );
};
